// Location utilities for fetching cities and states dynamically
import { US_CITIES, BR_CITIES } from './citiesData';

/**
 * Fetch cities from OpenDataSoft API for US states
 * Uses GeoNames data with cities > 1000 population
 * @param {string} stateCode - State code (e.g., 'CA', 'TX')
 * @returns {Promise<string[]>} - Array of city names
 */
export const fetchAmericanCities = async (stateCode) => {
  if (!stateCode) return [];
  
  try {
    const response = await fetch(
      `https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/geonames-all-cities-with-a-population-1000/records?where=country_code%3D%27US%27%20and%20admin1_code%3D%27${stateCode}%27&order_by=name&limit=100`
    );
    
    if (!response.ok) throw new Error('Failed to fetch US cities');
    
    const data = await response.json();
    return data.results.map(city => city.name);
  } catch (error) {
    console.error('Error fetching US cities:', error);
    // Fallback to hardcoded cities
    return US_CITIES[stateCode] || [];
  }
};

// US States
export const US_STATES = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
};

// Brazilian States
export const BR_STATES = {
  'AC': 'Acre',
  'AL': 'Alagoas',
  'AP': 'Amapá',
  'AM': 'Amazonas',
  'BA': 'Bahia',
  'CE': 'Ceará',
  'DF': 'Distrito Federal',
  'ES': 'Espírito Santo',
  'GO': 'Goiás',
  'MA': 'Maranhão',
  'MT': 'Mato Grosso',
  'MS': 'Mato Grosso do Sul',
  'MG': 'Minas Gerais',
  'PA': 'Pará',
  'PB': 'Paraíba',
  'PR': 'Paraná',
  'PE': 'Pernambuco',
  'PI': 'Piauí',
  'RJ': 'Rio de Janeiro',
  'RN': 'Rio Grande do Norte',
  'RS': 'Rio Grande do Sul',
  'RO': 'Rondônia',
  'RR': 'Roraima',
  'SC': 'Santa Catarina',
  'SP': 'São Paulo',
  'SE': 'Sergipe',
  'TO': 'Tocantins'
};

// Country codes to full names
export const COUNTRIES = {
  'US': 'United States',
  'BR': 'Brazil',
  'MX': 'Mexico',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'ES': 'Spain',
  'FR': 'France',
  'DE': 'Germany',
  'IT': 'Italy',
  'AR': 'Argentina',
  'CO': 'Colombia',
  'PE': 'Peru',
  'CL': 'Chile',
  'VE': 'Venezuela',
  'EC': 'Ecuador',
  'PT': 'Portugal',
  'CN': 'China',
  'IN': 'India',
  'JP': 'Japan',
  'KR': 'South Korea',
  'AU': 'Australia'
};

/**
 * Get state full name based on country and state code
 */
export const getStateName = (stateCode, countryCode) => {
  if (!stateCode) return stateCode;
  
  if (countryCode === 'US') {
    return US_STATES[stateCode] || stateCode;
  } else if (countryCode === 'BR') {
    return BR_STATES[stateCode] || stateCode;
  }
  
  return stateCode;
};

/**
 * Get country full name from country code
 */
export const getCountryName = (countryCode) => {
  if (!countryCode) return countryCode;
  return COUNTRIES[countryCode] || countryCode;
};

/**
 * Get cities for a given state and country
 * @param {string} stateCode - State code (e.g., 'CA', 'SP')
 * @param {string} countryCode - Country code (e.g., 'US', 'BR')
 * @returns {string[]} - Array of city names
 */
export const getCitiesByState = (stateCode, countryCode) => {
  if (!stateCode || !countryCode) return [];
  
  if (countryCode === 'US') {
    return US_CITIES[stateCode] || [];
  } else if (countryCode === 'BR') {
    return BR_CITIES[stateCode] || [];
  }
  
  return [];
};

/**
 * Fetch cities from IBGE API for Brazilian states
 * @param {string} stateCode - State code (e.g., 'SP', 'RJ')
 * @returns {Promise<string[]>} - Array of city names
 */
export const fetchBrazilianCities = async (stateCode) => {
  if (!stateCode) return [];
  
  try {
    const response = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios?orderBy=nome`
    );
    
    if (!response.ok) throw new Error('Failed to fetch cities');
    
    const data = await response.json();
    return data.map(city => city.nome);
  } catch (error) {
    console.error('Error fetching IBGE cities:', error);
    // Fallback to hardcoded cities
    return BR_CITIES[stateCode] || [];
  }
};
